import { useState, useEffect, useRef } from 'react';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, FileText, Search, Trash2, Upload, Eye, Edit2, Lock } from 'lucide-react';

// DocumentForm defined OUTSIDE the main component to prevent re-render issues
const DocumentForm = ({ 
  data, 
  onChange, 
  onSubmit, 
  horses, 
  selectedFile, 
  setSelectedFile, 
  fileInputRef,
  isEdit = false 
}) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Title *</Label>
      <Input 
        value={data.title} 
        onChange={(e) => onChange({ ...data, title: e.target.value })} 
        required 
        placeholder="e.g., 2024 Registration Papers"
        data-testid={isEdit ? "edit-doc-title-input" : "doc-title-input"}
      />
    </div>
    <div className="space-y-2">
      <Label>Document Type *</Label>
      <Select value={data.document_type} onValueChange={(v) => onChange({ ...data, document_type: v, custom_type: v === 'other' ? data.custom_type : '' })}>
        <SelectTrigger data-testid={isEdit ? "edit-doc-type-select" : "doc-type-select"}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="registration">Registration</SelectItem>
          <SelectItem value="vet_report">Vet Report</SelectItem>
          <SelectItem value="contract">Contract</SelectItem>
          <SelectItem value="insurance">Insurance</SelectItem>
          <SelectItem value="passport">Passport</SelectItem>
          <SelectItem value="xray">X-Ray</SelectItem>
          <SelectItem value="breeding">Breeding</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
    </div>
    {data.document_type === 'other' && (
      <div className="space-y-2">
        <Label>Custom Document Type *</Label>
        <Input 
          value={data.custom_type || ''} 
          onChange={(e) => onChange({ ...data, custom_type: e.target.value })} 
          required 
          placeholder="Enter document type label"
          data-testid="doc-custom-type-input"
        />
      </div>
    )}
    <div className="space-y-2">
      <Label>Horse</Label>
      <Select value={data.horse_id || 'general'} onValueChange={(v) => onChange({ ...data, horse_id: v === 'general' ? '' : v })}>
        <SelectTrigger data-testid={isEdit ? "edit-doc-horse-select" : "doc-horse-select"}>
          <SelectValue placeholder="General (no horse)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General (no horse)</SelectItem>
          {horses.map(horse => (
            <SelectItem key={horse.horse_id} value={horse.horse_id}>
              {horse.stable_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label>File {isEdit ? '(upload new to replace)' : '(optional)'}</Label>
      <div 
        className={`dropzone ${selectedFile ? 'border-forest bg-forest/5' : ''}`}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {selectedFile ? (
            <span className="text-forest font-medium">{selectedFile.name}</span>
          ) : isEdit && data.file_url ? (
            <span className="text-forest">Current file uploaded - Click to change</span>
          ) : (
            'Click to upload or drag & drop'
          )}
        </p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
        className="hidden"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      />
    </div>
    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid={isEdit ? "submit-edit-doc-btn" : "submit-doc-btn"}>
      {isEdit ? 'Update Document' : 'Add Document'}
    </Button>
  </form>
);

export const DocumentsPage = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [documents, setDocuments] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHorse, setFilterHorse] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  const emptyDoc = {
    title: '',
    document_type: 'registration',
    horse_id: '',
    custom_type: ''
  };
  
  const [newDoc, setNewDoc] = useState(emptyDoc);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [docsRes, horsesRes] = await Promise.all([
        fetch(`${API}/documents`, { headers }),
        fetch(`${API}/horses`, { headers })
      ]);

      if (docsRes.ok) setDocuments(await docsRes.json());
      if (horsesRes.ok) setHorses(await horsesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleAddDocument = async (e) => {
    e.preventDefault();
    
    // Validate custom type if "other" is selected
    if (newDoc.document_type === 'other' && !newDoc.custom_type?.trim()) {
      toast.error('Please enter a custom document type label');
      return;
    }

    try {
      const payload = {
        ...newDoc,
        horse_id: newDoc.horse_id || null  // null for general document
      };
      
      const response = await fetch(`${API}/documents`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const doc = await response.json();
        
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          
          const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
          const uploadHeaders = {};
          if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
          if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
          
          await fetch(`${API}/upload/documents/${doc.document_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
          
          await fetchData();
        } else {
          setDocuments([...documents, doc]);
        }
        
        setDialogOpen(false);
        setNewDoc(emptyDoc);
        setSelectedFile(null);
        toast.success('Document added!');
      }
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  const handleEditDocument = async (e) => {
    e.preventDefault();
    if (!editingDoc) return;

    try {
      const response = await fetch(`${API}/documents/${editingDoc.document_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          title: editingDoc.title,
          document_type: editingDoc.document_type,
          horse_id: editingDoc.horse_id
        })
      });

      if (response.ok) {
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          
          const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
          const uploadHeaders = {};
          if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
          if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
          
          await fetch(`${API}/upload/documents/${editingDoc.document_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
        }
        
        await fetchData();
        setEditDialogOpen(false);
        setEditingDoc(null);
        setSelectedFile(null);
        toast.success('Document updated!');
      }
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    
    try {
      const response = await fetch(`${API}/documents/${docId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setDocuments(documents.filter(d => d.document_id !== docId));
        toast.success('Document deleted');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const openEditDialog = (doc) => {
    setEditingDoc(doc);
    setSelectedFile(null);
    setEditDialogOpen(true);
  };

  const getHorseName = (horseId) => {
    if (!horseId) return 'General';
    const horse = horses.find(h => h.horse_id === horseId);
    return horse?.stable_name || 'Unknown';
  };

  const getDocTypeColor = (type) => {
    const colors = {
      registration: 'bg-forest/10 text-forest',
      vet_report: 'bg-saddle/10 text-saddle',
      contract: 'bg-terracotta/10 text-terracotta',
      insurance: 'bg-blue-100 text-blue-700',
      passport: 'bg-purple-100 text-purple-700',
      xray: 'bg-gray-100 text-gray-700',
      breeding: 'bg-pink-100 text-pink-700',
      other: 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  const filteredDocs = documents.filter(doc => {
    // Text search filter
    const matchesSearch = !searchTerm || 
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.document_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.custom_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Horse filter
    let matchesHorse = true;
    if (filterHorse === 'general') {
      matchesHorse = !doc.horse_id;
    } else if (filterHorse !== 'all') {
      matchesHorse = doc.horse_id === filterHorse;
    }
    
    // Document type filter
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    
    return matchesSearch && matchesHorse && matchesType;
  });

  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    const horseName = getHorseName(doc.horse_id);
    if (!acc[horseName]) acc[horseName] = [];
    acc[horseName].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-8 w-32 skeleton rounded-lg" />
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Document Vault</h1>
            <p className="text-muted-foreground mt-1">
              {documents.length} documents stored
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setNewDoc(emptyDoc); setSelectedFile(null); } }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                data-testid="add-document-btn"
                onClick={(e) => {
                  if (!canAddEdit()) {
                    e.preventDefault();
                    requireSubscription();
                  }
                }}
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" /> Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Document</DialogTitle>
              </DialogHeader>
              <DocumentForm 
                data={newDoc} 
                onChange={setNewDoc} 
                onSubmit={handleAddDocument}
                horses={horses}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                fileInputRef={fileInputRef}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by title or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-docs-input"
            />
          </div>
          
          {/* Horse Filter */}
          <Select value={filterHorse} onValueChange={setFilterHorse}>
            <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="filter-horse-select">
              <SelectValue placeholder="Filter by horse" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Documents</SelectItem>
              <SelectItem value="general">General Only</SelectItem>
              {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">By Horse</div>
                  {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').map(horse => (
                    <SelectItem key={horse.horse_id} value={horse.horse_id}>
                      🐴 {horse.stable_name}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          
          {/* Document Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="filter-type-select">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="registration">Registration</SelectItem>
              <SelectItem value="vet_report">Vet Report</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
              <SelectItem value="insurance">Insurance</SelectItem>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="xray">X-Ray</SelectItem>
              <SelectItem value="breeding">Breeding</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Documents List */}
        {filteredDocs.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">
                {documents.length === 0 ? 'No Documents Yet' : 'No Matching Documents'}
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {documents.length === 0 
                  ? 'Store important documents like registrations, vet reports, contracts, and more.'
                  : 'Try adjusting your search or filters to find what you\'re looking for.'}
              </p>
              {documents.length === 0 && (
                <Button onClick={() => setDialogOpen(true)} className="mt-4 bg-forest hover:bg-forest-dark gap-2">
                  <Plus className="w-4 h-4" /> Add First Document
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedDocs).map(([horseName, docs]) => (
              <div key={horseName}>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <span className="text-2xl">🐴</span> {horseName}
                </h3>
                <div className="space-y-2">
                  {docs.map((doc, i) => (
                    <Card key={doc.document_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.03}s` }}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-muted">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={getDocTypeColor(doc.document_type)}>
                                {doc.document_type === 'other' && doc.custom_type 
                                  ? doc.custom_type 
                                  : doc.document_type.replace('_', ' ')}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {doc.upload_date && format(parseISO(doc.upload_date), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.file_url && (
                            <Button variant="outline" size="sm" asChild>
                              <a 
                                href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="gap-1"
                              >
                                <Eye className="w-4 h-4" /> View
                              </a>
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openEditDialog(doc)}
                            data-testid={`edit-doc-${doc.document_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                            data-testid={`delete-doc-${doc.document_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingDoc(null); setSelectedFile(null); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Document</DialogTitle>
            </DialogHeader>
            {editingDoc && (
              <DocumentForm 
                data={editingDoc} 
                onChange={setEditingDoc} 
                onSubmit={handleEditDocument}
                horses={horses}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                fileInputRef={editFileInputRef}
                isEdit={true} 
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
